<script lang="ts">
    import type {Criterion} from '../search-criteria/criteria.types';
    import type {SvelteComponentConstructor} from 'core/types';
    import type {ForceSetting} from 'shared/value-editors/kp-value-editor-force-settings/types';
    import {pipe} from 'core/utils';
    import {loc} from 'shared/utils/pipes';
    import {createEventDispatcher, onDestroy} from 'svelte';
    import {tooltip} from 'shared/ui-widgets/tooltip/use.tooltip';
    import {debounceTime} from 'rxjs/operators';
    import {cleanup, exists} from 'shared/utils/custom-utils';
    import {Subject} from 'rxjs';
    import {Disclosure, DisclosureButton, DisclosurePanel} from '@rgossiaux/svelte-headlessui';
    import KpValueEditor from 'shared/value-editors/kp-value-editor/KpValueEditor.svelte';
    import KpValueEditorForceSettings
        from 'shared/value-editors/kp-value-editor-force-settings/KpValueEditorForceSettings.svelte';
    import SearchFacetsAcceptableEditorOptionLabel from './SearchFacetsAcceptableEditorOptionLabel.svelte';
    import UIcon from 'shared/ui-widgets/uicons/UIcon.svelte';
    import {
        createValueEditorsLabelContext,
        setValueEditorLabelContext
    } from 'shared/value-editors/internal/label/value-editor-label-context';

    export let criterion: Criterion;
    export let initiallyExpanded = false;

    const isOfRangeValueEditorType = criterion.field.editor.type === 'range';
    const dispatch = createEventDispatcher<{ 'criterion-change': void }>();
    const debouncedCriterionChangedEvent$: Subject<void> = new Subject();

    // debounce for range editor to prevent multiple searches for every slider movement
    const subscription = debouncedCriterionChangedEvent$.pipe(debounceTime(isOfRangeValueEditorType ? 250 : 0)).subscribe(() => dispatch('criterion-change'));

    const facetLabelContext = createValueEditorsLabelContext(criterion.field.id, criterion.field.text);
    setValueEditorLabelContext(facetLabelContext);

    $: hasDescription = exists(criterion.field.description) && criterion.field.description.length > 0;

    onDestroy(() => {
        cleanup(subscription);
    });

    function dispatchCriterionChangedEvent() {
        debouncedCriterionChangedEvent$.next();
    }

    const singleAcceptableEditorSettings: ForceSetting<'single-acceptable'> = {
        type: 'single-acceptable',
        options: {
            switchToInlineModeThreshold: 0,
            optionLabelComponent: SearchFacetsAcceptableEditorOptionLabel as SvelteComponentConstructor<{ option }>
        },
    }

    const multipleAcceptableEditorSettings: ForceSetting<'multiple-acceptable'> = {
        type: 'multiple-acceptable',
        options: {
            switchToInlineModeThreshold: 0,
            showFirstCount: 5,
            optionLabelComponent: SearchFacetsAcceptableEditorOptionLabel as SvelteComponentConstructor<{ option }>
        }
    }
</script>

<Disclosure class="facet" defaultOpen={initiallyExpanded} let:open={expanded}>
    <DisclosureButton class="facet-name btn btn-default btn-xs btn-block" type="button"
                      use={[[tooltip, {enabled: hasDescription, role: 'tooltip', content: criterion.field.description ?? ''}]]}>

        <span class="facet-name-label" id={facetLabelContext.labelId}>
            {pipe(criterion.field, loc())}
        </span>

        <div class="icon-container" class:expanded aria-hidden="true">
            <UIcon icon="angle-small-down"/>
        </div>

        {#if hasDescription}
            <div class="description-container">
                <UIcon icon="info"/>
            </div>
        {/if}
    </DisclosureButton>

    {#if expanded}
        <!-- we need to expand manually, otherwise editors won't get initialized properly (e.g. range slider without color) -->
        <DisclosurePanel class="facet-keys" static>
            <KpValueEditorForceSettings
                    forceSettings={[singleAcceptableEditorSettings, multipleAcceptableEditorSettings]}>
                <KpValueEditor editorId={criterion.field.id}
                               editorName={criterion.field.id}
                               {...criterion.field.editor}
                               bind:model={criterion.value}
                               on:model-change={dispatchCriterionChangedEvent}/>
            </KpValueEditorForceSettings>
        </DisclosurePanel>
    {/if}
</Disclosure>

<style lang="less">
    @import (reference) "styles/portaro.variables.less";
    @import (reference) "styles/portaro.themes.less";

    .icon-container {
        display: inline-flex;
        transition: transform 0.2s;
        color: @themed-text-muted;

        &.expanded {
            transform: rotate(-180deg);
        }
    }

    :global {
        .facet {
            .facet-keys {
                padding: @spacing-s @spacing-sm;
            }

            .facet-name {
                position: relative;
                border-radius: 0;
                border: none;
                box-shadow: none;
                border-top: 1px solid @themed-border-default;
                text-transform: uppercase;
                display: flex;
                justify-content: center;
                align-items: center;
                gap: @spacing-xs;
                width: 100%;
                padding-top: @spacing-xs;
                padding-bottom: @spacing-s;
                text-align: center;
                margin-bottom: @spacing-sm;

                + .facet-keys {
                    margin-top: calc(@spacing-sm * -1);
                }

                .facet-name-label {
                    font-weight: 500;
                }

                .description-container {
                    position: absolute;
                    right: @spacing-s;
                    top: 50%;
                    transform: translateY(-50%);
                    color: @themed-text-muted;
                    font-size: @font-size-default;
                }
            }
        }
    }
</style>